// Modular ruleset registry. For Milestone 1 only "zimbabwe-standard" is Playable.
// Each entry defines metadata; gameplay logic lives in /game/engine.js (Zimbabwean Standard)
// and additional engines will be added per-ruleset in future milestones.

export const STATUS = {
  PLAYABLE: "Playable",
  BETA: "Beta",
  IN_TESTING: "In Testing",
  COMING_SOON: "Coming Soon",
};

// Generic minimax static evaluator used by additional engines.
function staticEval(state, perspective) {
  const opp = perspective === "A" ? "B" : "A";
  if (state.status !== "ACTIVE") {
    if (state.winner === perspective) return 100000;
    if (state.winner === opp) return -100000;
    return 0;
  }
  const cap = state.captures[perspective] - state.captures[opp];
  return cap * 10;
}

export function chooseAIMoveForRuleset(state, difficulty, rulesetId) {
  if (rulesetId === "zimbabwe-standard") {
    // eslint-disable-next-line global-require
    return require("./ai").chooseAIMove(state, difficulty);
  }
  // eslint-disable-next-line global-require
  const engine = require("./registry").getEngine(rulesetId);
  // eslint-disable-next-line global-require
  const { chooseMoveMinimax } = require("./aiCore");
  return chooseMoveMinimax({
    state,
    applyMove: engine.applyMove,
    getLegalMoves: engine.getLegalMoves,
    evaluate: staticEval,
    difficulty,
  });
}

export const RULESETS = [
  // Kenya — Mbothe
  {
    id: "kenya-mbothe",
    name: "Mbothe (Pokomo)",
    altNames: [],
    country: "Kenya",
    region: "East Africa",
    rows: 2,
    pitsPerRow: 10,
    counters: 40,
    status: STATUS.PLAYABLE,
    summary: "2×10 board. Two-counter protection — opponent pits of exactly 2 are skipped during sowing. Exact-2 opposite capture; capturer plays again.",
    keyRules: ["Two-counter protection.", "Exact-2 capture.", "Capturer plays again.", "Feeding rule."],
    engineId: "ke-mbothe-standard",
  },
  {
    id: "zimbabwe-standard",
    name: "Standard Tsoro",
    altNames: ["Fuva", "Imbwe"],
    country: "Zimbabwe",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 32,
    status: STATUS.PLAYABLE,
    summary: "Capture every opposing counter on a 4×8 board with relay sowing and column-paired captures.",
    keyRules: [
      "Outer rows start with 2 counters per pit; inner rows empty.",
      "Sow anticlockwise around your own two rows only.",
      "Relay continues until the final counter lands in a previously-empty pit.",
      "Capture when finishing in your empty inner pit and the opposite enemy inner pit has counters.",
      "Capture the opposite outer pit too if the inner was captured.",
      "Singleton restriction: cannot start from a 1-counter pit while any 2+ pit exists.",
    ],
    engineId: "zimbabwe-standard",
  },
  {
    id: "zimbabwe-additional-capture",
    name: "Additional-Capture Tsoro",
    altNames: [],
    country: "Zimbabwe",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 32,
    status: STATUS.IN_TESTING,
    summary: "Standard Tsoro plus 1–3 additional opponent pits removed after each capture.",
    keyRules: ["Same as Standard, plus N extra opponent pits removed after each capture."],
  },
  {
    id: "zimbabwe-reentered-capture",
    name: "Re-entered-Capture Tsoro",
    altNames: [],
    country: "Zimbabwe",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 32,
    status: STATUS.IN_TESTING,
    summary: "Captured counters are immediately re-sown on your own side, potentially causing chains.",
    keyRules: ["After a capture, sow the captured counters back from the pit following the trigger pit."],
  },
  {
    id: "zimbabwe-misoro",
    name: "Misoro Tsoro",
    altNames: [],
    country: "Zimbabwe",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 32,
    status: STATUS.COMING_SOON,
    summary: "Two designated outer pits per side can stop a relay, allowing tactical stockpiles.",
    keyRules: ["Two misoro per side. Relay finishing in your misoro is optional."],
  },
  {
    id: "zimbabwe-misoro-additional",
    name: "Misoro with Additional Captures",
    altNames: [],
    country: "Zimbabwe",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 32,
    status: STATUS.COMING_SOON,
    summary: "Combines Misoro pits with the agreed extra-pit captures rule.",
    keyRules: ["Misoro counters are protected from being selected as extra captures."],
  },
  {
    id: "mozambique-ntxuva",
    name: "Ntxuva (M’pale)",
    altNames: ["M’pale"],
    country: "Mozambique",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 64,
    status: STATUS.COMING_SOON,
    summary: "All pits start with 2 seeds. Capture the opposing column plus any 2 chosen enemy pits.",
    keyRules: ["Two seeds in every pit at start.", "Capture +2 chosen extra pits after each capture."],
  },
  {
    id: "zambia-malawi-nsolo",
    name: "Nsolo / Nchombwa",
    altNames: ["Nchombwa"],
    country: "Zambia & Malawi",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 60,
    status: STATUS.PLAYABLE,
    summary: "Rightmost inner pit starts empty. After a capture, remove an equal number more from anywhere on the opponent's side.",
    keyRules: ["Equal-bonus capture rule.", "Repeated position is a draw."],
    engineId: "mw-nchombwa-standard",
  },
  {
    id: "zambia-chisolo",
    name: "Chisolo (Natatu)",
    altNames: ["Natatu"],
    country: "Zambia",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 7,
    counters: 66,
    status: STATUS.PLAYABLE,
    summary: "4×7 board with a special opening arrangement. Player 1 sows clockwise; Player 2 chooses their direction on first move (locked). Capture takes opposite inner + paired outer + 1 free pit.",
    keyRules: ["Special opening on inner row.", "Player 1 clockwise; Player 2 locks direction on first move.", "Three-stage capture."],
    engineId: "zm-chisolo-natatu",
  },
  {
    id: "south-africa-moruba",
    name: "Moruba",
    altNames: [],
    country: "South Africa",
    region: "Southern Africa",
    rows: 4,
    pitsPerRow: 12,
    counters: 192,
    status: STATUS.COMING_SOON,
    summary: "4×12 board with 4 counters per pit. Player with the most captures wins.",
    keyRules: ["Highest capture total wins, not necessarily total elimination."],
  },
  {
    id: "uganda-omweso",
    name: "Omweso (Mweso)",
    altNames: ["Mweso"],
    country: "Uganda",
    region: "East Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 64,
    status: STATUS.COMING_SOON,
    summary: "Players arrange their own opening. Captured counters re-enter play.",
    keyRules: ["Free opening setup.", "Captured stones re-sown immediately on capturer's side."],
  },
  {
    id: "ghana-oware",
    name: "Oware Abapa",
    altNames: [],
    country: "Ghana",
    region: "West Africa",
    rows: 2,
    pitsPerRow: 6,
    counters: 48,
    status: STATUS.PLAYABLE,
    summary: "Classic 2×6 mancala. Capture when sowing leaves 2 or 3 in an opponent's pit. 25 wins.",
    keyRules: ["Two-row board.", "Capture on 2/3.", "Feeding rule mandatory."],
    engineId: "gh-oware-abapa",
  },
  {
    id: "nigeria-ayo",
    name: "Ayò Ọlọ́pọ́n",
    altNames: ["Ayo", "Ayoayo"],
    country: "Nigeria",
    region: "West Africa",
    rows: 2,
    pitsPerRow: 6,
    counters: 48,
    status: STATUS.PLAYABLE,
    summary: "2×6 board with relay sowing. Capture when the final seed lands in an empty pit on your own row and the opposite pit is occupied.",
    keyRules: ["Two-row board.", "Relay sowing.", "Empty-own-row landing captures opposite pit."],
    engineId: "ng-ayoayo-relay",
  },
  {
    id: "bao",
    name: "Bao",
    altNames: [],
    country: "Tanzania, Zanzibar, coastal Kenya",
    region: "East Africa",
    rows: 4,
    pitsPerRow: 8,
    counters: 64,
    status: STATUS.COMING_SOON,
    summary: "Advanced 4×8 game with reserves, a house pit, compulsory captures and a two-phase structure.",
    keyRules: ["Reserves and a 'house' pit.", "Captures are compulsory.", "Two-phase game."],
  },
];

export function getRuleset(id) {
  return RULESETS.find((r) => r.id === id) || null;
}

export const COUNTRY_GROUPS = [
  { slug: "zimbabwe", country: "Zimbabwe", rulesetIds: ["zimbabwe-standard", "zimbabwe-additional-capture", "zimbabwe-reentered-capture", "zimbabwe-misoro", "zimbabwe-misoro-additional"] },
  { slug: "mozambique", country: "Mozambique", rulesetIds: ["mozambique-ntxuva"] },
  { slug: "zambia-malawi", country: "Zambia & Malawi", rulesetIds: ["zambia-malawi-nsolo", "zambia-chisolo"] },
  { slug: "south-africa", country: "South Africa", rulesetIds: ["south-africa-moruba"] },
  { slug: "uganda", country: "Uganda", rulesetIds: ["uganda-omweso"] },
  { slug: "ghana", country: "Ghana", rulesetIds: ["ghana-oware"] },
  { slug: "nigeria", country: "Nigeria", rulesetIds: ["nigeria-ayo"] },
  { slug: "kenya", country: "Kenya", rulesetIds: ["kenya-mbothe"] },
  { slug: "bao", country: "Bao", rulesetIds: ["bao"] },
];

export function getCountryGroup(slug) {
  return COUNTRY_GROUPS.find((g) => g.slug === slug) || null;
}
